import { DOCUMENT } from '@angular/common';
import { Loader, Libraries, Library } from '@googlemaps/js-api-loader';
import { Injectable, Inject, Optional, InjectionToken } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject, firstValueFrom } from 'rxjs';
import { GoogleModuleOptions, GOOGLE_MAPS_API_CONFIG } from '../helpers/maps-helpers';
import { LogHelper } from '../../logging/log-helper';
import { PromiseCache } from '../../cache/promise.cache';
import { AppConfig } from 'src/app/app.config';
import { LocaleHelper } from 'src/app/l10n-config';

/** logging tag */
const tag = 'MapsApi';

/** For using a mock version of google maps (for tests, etc) */
export const MAPS_USE_MOCK = new InjectionToken<boolean>('useMockMaps');

/*
 * Configures google api outside of NgModule so that the apiKey can be set from an external config
 */
@Injectable()
export class GoogleMapsConfig implements GoogleModuleOptions {
  static readonly lazyLibraries: Library[] = ['core', 'maps', 'marker', 'places', 'geocoding', 'drawing'];
  
  apiKey = AppConfig.googleApiKey;
  libraries = ['core']; // additional libraries loaded lazily based on `lazyLibraries`
  language = LocaleHelper.parseLanguageCode();
  region = LocaleHelper.parseRegionCode();
}

@Injectable()
export abstract class MapsApiLoader {
  protected _window: Window;
  protected _document: Document;
  
  protected apiLoadedSubject = new BehaviorSubject<boolean>(false);
  public apiLoaded$: Observable<boolean> = this.apiLoadedSubject.asObservable();

  public abstract load(libraries?: Library[]): Promise<void>;

  /**
   * If no configuration is provided at load time you can use this function to provide configuration at any time.
   * Loading scripts will be postponed until a configuration is provided
   *
   * @param config - {@link GoogleModuleOptions} configuration needed for bootstrapping
   */
  protected abstract configure(config: unknown): void;
}

/** Loads the google maps API lazily. Used in AppModule. */
@Injectable()
export class LazyMapsApiLoader extends MapsApiLoader {
  protected _config: ReplaySubject<GoogleModuleOptions> = new ReplaySubject<GoogleModuleOptions>(1);
  private static readonly promises = new PromiseCache<unknown>();

  constructor(
    @Optional()
    @Inject(DOCUMENT) document: Document,
    @Inject(GOOGLE_MAPS_API_CONFIG) config: GoogleModuleOptions,
    @Inject(MAPS_USE_MOCK) private readonly useMockMaps: boolean = false,
  ) {
    super();
    LogHelper.debug(tag, `constructor ${useMockMaps ? 'mock' : ''}`);
    this._document = document;
    this._window = this._document.defaultView;
    
    this.configure(config);
  }

  protected configure(config: GoogleModuleOptions | Promise<GoogleModuleOptions>) {
    const onConfigReceived = function(loader: LazyMapsApiLoader, cfg: GoogleModuleOptions) {
      const { apiKey, ...logCfg } = cfg; // don't log apiKey
      LogHelper.debug(tag, 'onConfigReceived', logCfg);
      loader._config.next(cfg);
      loader._config.complete();
      void loader.load();
    }

    if (config instanceof Promise) {
      void config.then((c) => onConfigReceived(this, c));
    } else if (typeof config === 'object') {
      onConfigReceived(this, config);
    }
  }

  public async load(libraries?: Library[]): Promise<void> {
    const config = await firstValueFrom(this._config);
    const resolvedApiKey = this.useMockMaps ? 'mock-api-key' : config.apiKey;
    const loader = new Loader({
      apiKey: resolvedApiKey,
      version: config.apiVersion || 'weekly',
      libraries: config.libraries as Libraries,
      client: config.clientId,
      channel: config.channel,
      region: config.region,
      language: config.language,
    });

    if (!libraries) libraries = GoogleMapsConfig.lazyLibraries;
    const cache = LazyMapsApiLoader.promises;
    await Promise.all(libraries.map(async library => cache.get(library, () => {
      LogHelper.debug(tag, `loading: ${library}`);
      return loader.importLibrary(library);
    })));

    this.apiLoadedSubject.next(true);
  }
}